import React, { useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { X, Upload, FileJson, CheckCircle2, AlertTriangle, Copy } from "lucide-react";

/**
 * JSON toplu soru import modalı.
 *
 * Akış:
 *  1) JSON'u yapıştır veya dosya seç
 *  2) "JSON'u Kontrol Et" → backend dry_run → önizleme + hatalar + duplicate
 *  3) Kontrol geçtiyse "İçe Aktar" aktifleşir
 *
 * Bu bileşen, mevcut Yeni Soru editörünün hiçbir alanına dokunmaz.
 */
export default function BulkImportModal({ open, onClose, onImported }) {
  const [rawText, setRawText] = useState("");
  const [checking, setChecking] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null); // dry_run response
  const [imported, setImported] = useState(null); // final import response
  const fileRef = useRef(null);

  const reset = () => {
    setRawText("");
    setResult(null);
    setImported(null);
  };

  const close = () => {
    reset();
    onClose && onClose();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setRawText(text);
      setResult(null);
      setImported(null);
    } catch {
      toast.error("Dosya okunamadı");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const runCheck = async () => {
    setImported(null);
    let payload;
    try {
      payload = JSON.parse(rawText);
    } catch (e) {
      toast.error("JSON söz dizimi hatalı: " + (e.message || "bilinmeyen hata"));
      setResult({ syntax_error: e.message || "JSON parse hatası" });
      return;
    }
    setChecking(true);
    try {
      const { data } = await api.post("/questions/bulk-import?dry_run=true", payload);
      setResult(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Kontrol sırasında hata oluştu";
      toast.error(detail);
      setResult({ server_error: detail });
    } finally {
      setChecking(false);
    }
  };

  const runImport = async () => {
    let payload;
    try {
      payload = JSON.parse(rawText);
    } catch {
      toast.error("JSON söz dizimi hatalı");
      return;
    }
    setImporting(true);
    try {
      const { data } = await api.post("/questions/bulk-import", payload);
      setImported(data);
      toast.success(`${data.summary.imported ?? 0} soru içe aktarıldı`);
      onImported && onImported(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İçe aktarma başarısız");
    } finally {
      setImporting(false);
    }
  };

  const canImport = useMemo(() => {
    if (!result || result.syntax_error || result.server_error) return false;
    if (imported) return false; // already imported this batch
    const s = result.summary || {};
    return (s.importable || 0) > 0 && (s.invalid || 0) === 0;
  }, [result, imported]);

  const pasteSample = () => {
    const sample = {
      sinav_seviyesi: "TYT",
      sorular: [
        {
          external_id: "SAMPLE_20260901_001",
          icerik_bloklari: [
            { tip: "latex", icerik: "4\\sqrt{6}+2\\sqrt{6}-3\\sqrt{6}" },
            { tip: "metin", icerik: "işleminin sonucu kaçtır?" },
          ],
          siklar: [
            { harf: "A", metin: "2\\sqrt{6}" },
            { harf: "B", metin: "3\\sqrt{6}" },
            { harf: "C", metin: "4\\sqrt{6}" },
            { harf: "D", metin: "5\\sqrt{6}" },
            { harf: "E", metin: "6\\sqrt{6}" },
          ],
          dogru_cevap: "B",
          tagler: [
            { konu: "Köklü Sayılar", alt_konu: "Kök Dışına Çıkarma", tag: "Köklü Sayılarda Sadeleştirme", importance: "high" },
          ],
          cozum: "4√6 + 2√6 - 3√6 = 3√6",
          kazanim: "Köklü ifadelerde işlem yapar.",
        },
      ],
    };
    setRawText(JSON.stringify(sample, null, 2));
    setResult(null);
    setImported(null);
  };

  if (!open) return null;

  const s = result?.summary || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start sm:items-center justify-center p-2 sm:p-6"
         data-testid="bulk-import-modal">
      <div className="w-full max-w-4xl rounded-xl bg-[#0F0F0F] border border-[#262626] shadow-2xl max-h-[95vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#262626]">
          <div className="flex items-center gap-2">
            <FileJson size={18} className="text-[#EA580C]" />
            <div className="font-display text-lg">JSON Toplu Soru Import</div>
          </div>
          <button onClick={close} className="text-slate-400 hover:text-white"
            data-testid="bulk-import-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-auto">
          {/* Input area */}
          <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-start">
            <div>
              <div className="text-xs text-slate-400 mb-1">JSON metnini yapıştır</div>
              <textarea
                data-testid="bulk-import-textarea"
                value={rawText}
                onChange={(e) => { setRawText(e.target.value); setResult(null); setImported(null); }}
                rows={10}
                placeholder={'[ JSON\'u buraya yapıştır ]'}
                className="w-full font-mono text-xs px-3 py-2 rounded-md bg-[#0A0A0A] border border-[#262626] focus:border-[#EA580C] outline-none min-h-[180px]"
              />
            </div>
            <div className="flex sm:flex-col gap-2 sm:min-w-[180px]">
              <button type="button" onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#171717] border border-[#262626] hover:border-[#EA580C] text-sm"
                data-testid="bulk-import-file-btn">
                <Upload size={14} /> Dosya seç
              </button>
              <input ref={fileRef} type="file" accept="application/json,.json"
                onChange={onFile} className="hidden" data-testid="bulk-import-file-input" />
              <button type="button" onClick={pasteSample}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#171717] border border-[#262626] hover:border-[#EA580C] text-sm text-slate-300"
                data-testid="bulk-import-sample-btn">
                <Copy size={14} /> Örnek yapıştır
              </button>
              <button type="button" onClick={runCheck} disabled={!rawText.trim() || checking}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#EA580C] hover:bg-[#F97316] text-white text-sm disabled:opacity-50"
                data-testid="bulk-import-check-btn">
                {checking ? "Kontrol ediliyor…" : "JSON'u Kontrol Et"}
              </button>
            </div>
          </div>

          {/* Errors from client-side JSON.parse or server 400 */}
          {result?.syntax_error && (
            <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200"
                 data-testid="bulk-import-syntax-error">
              <div className="font-medium">JSON söz dizimi hatalı</div>
              <div className="font-mono text-xs mt-1">{result.syntax_error}</div>
            </div>
          )}
          {result?.server_error && (
            <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200"
                 data-testid="bulk-import-server-error">
              {result.server_error}
            </div>
          )}

          {/* Summary */}
          {result?.summary && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm"
                 data-testid="bulk-import-summary">
              <SummaryChip label="Sınav" value={s.exam_level} />
              <SummaryChip label="Toplam" value={s.total} />
              <SummaryChip label="Geçerli" value={s.valid} tone="ok" />
              <SummaryChip label="Hatalı" value={s.invalid} tone={s.invalid ? "err" : "muted"} />
              <SummaryChip label="Duplicate" value={s.duplicates} tone={s.duplicates ? "warn" : "muted"} />
            </div>
          )}

          {/* Invalid items */}
          {result?.invalid_items?.length > 0 && (
            <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-sm"
                 data-testid="bulk-import-invalid-list">
              <div className="flex items-center gap-2 text-rose-200 font-medium">
                <AlertTriangle size={14} /> Hatalı kayıtlar ({result.invalid_items.length})
              </div>
              <ul className="mt-1 space-y-1 text-rose-100/90 font-mono text-xs max-h-40 overflow-auto pr-2">
                {result.invalid_items.map((it) => (
                  <li key={it.index}>
                    <div className="font-medium">Soru #{it.index}</div>
                    <ul className="ml-4 list-disc">
                      {(it.errors || []).map((er, k) => <li key={k}>{er}</li>)}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Duplicate items */}
          {result?.duplicate_items?.length > 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-100"
                 data-testid="bulk-import-duplicate-list">
              <div className="font-medium flex items-center gap-2 text-amber-200">
                <AlertTriangle size={14} /> Duplicate ({result.duplicate_items.length}) — bunlar atlanacak
              </div>
              <ul className="mt-1 font-mono text-xs max-h-40 overflow-auto space-y-0.5">
                {result.duplicate_items.map((it, i) => (
                  <li key={i}>
                    #{it.index} · <span className="text-amber-300">{it.external_id}</span>
                    {it.ref ? <> · mevcut: <span className="text-amber-200">{it.ref}</span></> : null}
                    {it.reason === "batch" ? " · (aynı JSON içinde tekrar)" : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Curriculum catalog warnings (non-blocking) */}
          {result?.curriculum_warnings?.length > 0 && (
            <div className="rounded-md border border-sky-500/40 bg-sky-500/10 p-3 text-sm text-sky-100"
                 data-testid="bulk-import-curriculum-warnings">
              <div className="font-medium flex items-center gap-2 text-sky-200">
                <AlertTriangle size={14} /> Müfredat kataloğu uyarıları ({result.curriculum_warnings.length}) — import engellenmez
              </div>
              <ul className="mt-1 font-mono text-xs max-h-40 overflow-auto space-y-0.5">
                {result.curriculum_warnings.map((w, i) => (
                  <li key={i} data-testid={`bulk-import-curriculum-warning-${i}`}>{w.message}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Previews */}
          {result?.previews?.length > 0 && (
            <div className="rounded-md border border-[#262626] bg-[#0A0A0A] p-3"
                 data-testid="bulk-import-previews">
              <div className="text-sm font-medium mb-2">İçe Aktarılacak Sorular ({result.previews.length})</div>
              <div className="max-h-64 overflow-auto space-y-2">
                {result.previews.map((p) => (
                  <div key={p.index} className="text-xs border border-[#1f1f1f] rounded p-2">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-slate-500">#{p.index}</span>
                      {p.external_id && (
                        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-[#171717] border border-[#262626]">
                          {p.external_id}
                        </span>
                      )}
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#EA580C]/20 text-[#EA580C]">
                        {p.exam_level}
                      </span>
                      <span className="text-slate-300">{p.konu}{p.alt_konu ? ` · ${p.alt_konu}` : ""}</span>
                      <span className="ml-auto text-emerald-300">Doğru: {p.correct}</span>
                    </div>
                    <div className="mt-1 text-slate-400 line-clamp-2">{p.content_preview || <i>—</i>}</div>
                    {p.tags?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {p.tags.map((t, k) => (
                          <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-[#171717] border border-[#262626]">
                            {t.tag} · <span className="text-slate-500">{t.importance}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Import result */}
          {imported && (
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-100"
                 data-testid="bulk-import-done">
              <div className="flex items-center gap-2 text-emerald-200 font-medium">
                <CheckCircle2 size={14} /> İçe aktarma tamamlandı — {imported.summary.imported ?? 0} soru eklendi
              </div>
              {imported.created_refs && (
                <div className="mt-1 text-xs text-emerald-200/80">
                  Yeni oluşturulanlar → Konu: {imported.created_refs.topics} ·
                  Alt Konu: {imported.created_refs.subtopics} · Tag: {imported.created_refs.tags}
                </div>
              )}
              {imported.imported?.length > 0 && (
                <ul className="mt-2 font-mono text-xs max-h-40 overflow-auto space-y-0.5">
                  {imported.imported.map((it) => (
                    <li key={it.question_id}>
                      #{it.index} → <span className="text-emerald-300">{it.ref}</span>
                      {it.external_id ? ` · ${it.external_id}` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#262626] bg-[#0A0A0A]">
          <button onClick={close} className="px-3 py-2 rounded-md bg-[#171717] border border-[#262626] text-sm"
            data-testid="bulk-import-cancel">Kapat</button>
          <button onClick={runImport} disabled={!canImport || importing}
            className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm disabled:opacity-40"
            data-testid="bulk-import-submit">
            {importing ? "İçe aktarılıyor…" : "İçe Aktar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryChip({ label, value, tone }) {
  const toneCls =
    tone === "ok" ? "text-emerald-300" :
    tone === "err" ? "text-rose-300" :
    tone === "warn" ? "text-amber-300" :
    "text-slate-300";
  return (
    <div className="rounded-md bg-[#0A0A0A] border border-[#262626] px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-sm font-mono ${toneCls}`}>{value ?? "-"}</div>
    </div>
  );
}
